import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { fetch } from "../utils/api-wrapper";
import { cachedFetch } from "../utils/cached-api-wrapper";
import { getSelectStyles } from "../utils/utils";
import {
  matterFromSearchHit,
  matterSearchUrl,
  normalizeMatterRestriction,
  toMatterSelectOption,
} from "../utils/matter-search";
import { renderSelectionChipRow, selectionChipListStyle } from "../utils/selection-chip-styles";

const MODES = ["all", "only", "except"];

/**
 * XPEN-3758: a category's Matter rule — "All Matters" (stored as `null`),
 * "Only these Matters" or "All Matters except these" (`{ mode, matter_numbers }`,
 * the XPEN-3757 contract). The numbers are each Matter's `number`, which is what
 * the server compares.
 *
 * It does not use `buildMultiSelectPickerParts`: that factory rebuilds the value
 * from a fixed options list, so a Matter found by an on-demand search would drop
 * out of the value on the next change. Only its chip rows are reused.
 *
 * The search is the plain Matter search, with no category id: an admin can pick
 * any active Matter the firm-wide hidden list does not hide.
 *
 * @param {object}   data        - MobX store (labels, Matter display flags)
 * @param {object}   value       - the stored rule, or null
 * @param {Function} onChange    - (nextRule | null) => void
 * @param {string}   inputId     - search box id; the mode dropdown is `${inputId}-mode`
 * @param {string}   chipTestId  - data-testid on the chip list
 * @param {boolean}  editAllowed - false disables both dropdowns and hides remove buttons
 */
const MatterRestrictionPicker = ({ data, value, onChange, inputId, chipTestId, editAllowed }) => {
  const restriction = normalizeMatterRestriction(value);
  const numbers = restriction?.matter_numbers ?? [];
  const numbersKey = JSON.stringify(numbers);
  const [labels, setLabels] = useState({});
  // Numbers already looked up (found or not), or picked with their label: each
  // saved number costs at most one lookup while this picker is mounted (the
  // request cache shares answers between pickers).
  const triedRef = useRef(new Set());
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    JSON.parse(numbersKey).forEach((number) => {
      if (triedRef.current.has(number)) return;
      triedRef.current.add(number);
      cachedFetch(matterSearchUrl(number), "GET", {}, (response) => {
        const hits = Array.isArray(response?.data) ? response.data : [];
        // The rule lists Matter numbers, so only a hit with that number labels
        // it, not one whose alternate number happens to match.
        const exact = hits.map(matterFromSearchHit).find((m) => m && String(m.number) === number);
        const label = exact ? toMatterSelectOption(data, exact)?.label : null;
        if (label && isMountedRef.current) setLabels((prev) => ({ ...prev, [number]: label }));
      }, () => {});
    });
  }, [numbersKey, data]);

  const modeOptions = MODES.map((mode) => ({ value: mode, label: data.t.read(`imputation_matter_restriction_${mode}`) }));
  const placeholder = data.t.read("imputation_matter_restriction_placeholder");

  const changeMode = (choice) => {
    const mode = choice?.value ?? "all";
    onChange(mode === "all" ? null : { mode, matter_numbers: numbers });
  };

  const loadOptions = (text, callback) => {
    const query = String(text ?? "").trim();
    if (!query) {
      callback([]);
      return;
    }
    fetch(matterSearchUrl(query), "GET", {}, (response) => {
      const hits = Array.isArray(response?.data) ? response.data : [];
      callback(
        hits
          .map((hit) => toMatterSelectOption(data, hit))
          .filter((option) => option && !numbers.includes(String(option.value.number)))
      );
    }, () => callback([]));
  };

  const pick = (option) => {
    const number = option?.value?.number == null ? "" : String(option.value.number);
    if (!number || numbers.includes(number)) return;
    triedRef.current.add(number);
    setLabels((prev) => ({ ...prev, [number]: option.label }));
    onChange({ mode: restriction.mode, matter_numbers: [...numbers, number] });
  };

  return (
    <>
      <Select
        inputId={`${inputId}-mode`}
        classNamePrefix="select"
        isDisabled={!editAllowed}
        value={modeOptions.find((o) => o.value === (restriction?.mode ?? "all"))}
        options={modeOptions}
        onChange={changeMode}
        styles={getSelectStyles(false, false)}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      {restriction && (
        <div className="mt-2">
          <AsyncSelect
            inputId={inputId}
            aria-label={placeholder}
            classNamePrefix="select"
            isDisabled={!editAllowed}
            value={null}
            controlShouldRenderValue={false}
            loadOptions={loadOptions}
            onChange={pick}
            noOptionsMessage={() => data.t.read("review_expense.matter.type_to_search")}
            placeholder={placeholder}
            styles={getSelectStyles(false, false)}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      )}
      {numbers.length > 0 && (
        <div style={selectionChipListStyle()} data-testid={chipTestId}>
          {numbers.map((number) =>
            renderSelectionChipRow({
              key: number,
              label: labels[number] ?? number,
              canEdit: editAllowed,
              onRemove: () => onChange({ mode: restriction.mode, matter_numbers: numbers.filter((n) => n !== number) }),
              removeLabel: data.t.read("delete"),
            })
          )}
        </div>
      )}
    </>
  );
};

export default MatterRestrictionPicker;
